'use client';

import { useCallback, useEffect, useRef } from 'react';

interface UseLongPressOptions {
  /**
   * Duration in milliseconds for long press detection
   */
  duration?: number;
  /**
   * Callback fired when long press is detected
   */
  onLongPress: () => void;
  /**
   * Optional callback fired on press start
   */
  onPressStart?: () => void;
  /**
   * Optional callback fired on press end
   */
  onPressEnd?: () => void;
}

interface UseLongPressReturn {
  /**
   * Props to spread on the target element
   */
  onMouseDown: () => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchStart: () => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
  onClick: (event: React.MouseEvent) => void;
}

/**
 * Custom hook for handling long press interactions
 * @param options Configuration options for the long press behavior
 * @returns Event handlers to attach to the target element
 */
export const useLongPress = ({
  duration = 3000,
  onLongPress,
  onPressStart,
  onPressEnd,
}: UseLongPressOptions): UseLongPressReturn => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPressRef = useRef(false);
  const preventClickRef = useRef(false);

  const startPress = useCallback(() => {
    isLongPressRef.current = false;
    preventClickRef.current = false;
    onPressStart?.();

    timeoutRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      preventClickRef.current = true;
      onLongPress();
    }, duration);
  }, [duration, onLongPress, onPressStart]);

  const endPress = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!isLongPressRef.current) {
      onPressEnd?.();
    }
  }, [onPressEnd]);

  const handleClick = useCallback((event: React.MouseEvent) => {
    if (preventClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Reset flags after handling click
    isLongPressRef.current = false;
    preventClickRef.current = false;
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    onClick: handleClick,
    onMouseDown: startPress,
    onMouseLeave: endPress,
    onMouseUp: endPress,
    onTouchCancel: endPress,
    onTouchEnd: endPress,
    onTouchStart: startPress,
  };
};
